import { useState, useRef, useEffect, useCallback } from 'react';
import { sendChatMessage } from '../services/chatService';
import type { Message } from '../types/assets';
import { v4 as uuidv4 } from 'uuid';
import { getCookie, setCookie } from '../utils/cookieUtils';

interface UseChatProps {
  chatId?: string;
  maxMessages?: number;
}

interface UseChatReturn {
  messages: Message[];
  sendMessage: (text: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export const useChat = ({ chatId, maxMessages = 10 }: UseChatProps = {}): UseChatReturn => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialMessageSent = useRef(false);

  // Generate senderId and manage cookie
  const [senderId] = useState<string>(() => {
    const existingSenderId = getCookie('senderId');
    if (existingSenderId) {
      return existingSenderId;
    } else {
      const newSenderId = uuidv4();
      setCookie('senderId', newSenderId, { days: 365 });
      return newSenderId;
    }
  });

  // Send initial message to get greeting from AI
  useEffect(() => {
    const sendInitialMessage = async () => {
      if (!initialMessageSent.current) {
        initialMessageSent.current = true;
        try {
          const botResponse = await sendChatMessage(chatId, [], senderId);
          if (botResponse.trim() !== '') {
            setMessages([{ text: botResponse, isUser: false, id: uuidv4(), timestamp: new Date() }]);
          }
        } catch (err: any) {
          console.error('Error fetching initial message:', err);
          // Fallback to welcome message for EFSoft website
          const fallbackMessage = "Hello! Welcome to EFSoft. How can I assist you with your software development needs today?";
          setMessages([{ text: fallbackMessage, isUser: false, id: uuidv4(), timestamp: new Date() }]);
        }
      }
    };

    sendInitialMessage();
  }, [chatId, senderId]);

  // Add a new user message and get bot response
  const sendMessage = useCallback(async (messageText: string): Promise<void> => {
    setError(null);
    setIsLoading(true);
    const trimmedMessage = messageText.trim();

    // --- Handle initial empty message --- 
    if (trimmedMessage === '') {
      try {
        const botResponse = await sendChatMessage(chatId, [], senderId); // Send empty array
        // Use functional update to avoid race condition if initial message is already set
        setMessages(prevMessages => {
          if (botResponse.trim() !== '' && prevMessages.length === 0) {
            return [{ text: botResponse, isUser: false, id: uuidv4(), timestamp: new Date() }];
          }
          return prevMessages; // No change needed
        });
      } catch (err: any) {
        console.error('Error fetching initial message:', err);
        // Fallback to mock response for EFSoft website
        const fallbackMessage = "Hello! Welcome to EFSoft. How can I assist you with your software development needs today?";
        setMessages([{ text: fallbackMessage, isUser: false, id: uuidv4(), timestamp: new Date() }]);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // --- Handle regular message sending --- 
    const userMessage: Message = { text: trimmedMessage, isUser: true, id: uuidv4(), timestamp: new Date() };

    // Prepare history: New message + text of previous 2 messages
    const previousMessagesTexts = messages
      .slice(-2) // Get last 2 messages from current state
      .map(m => m.text).reverse();
      
    // Construct the history array: [newMessage, prev1, prev2]
    const history = [trimmedMessage, ...previousMessagesTexts];
    
    // Pad with empty strings up to 3 elements if needed
    // (Backend expects 3 elements: new message + up to 2 previous)
    while (history.length < 3) {
        history.push(''); 
    }

    // Add user message to the state immediately (optimistic update)
    // Use functional update for safety
    setMessages(prevMessages => {
        const newMessages = [...prevMessages, userMessage];
        // Enforce maxMessages limit
        if (newMessages.length > maxMessages) {
            return newMessages.slice(-maxMessages);
        }
        return newMessages;
    });

    try {
      // Send to API with the prepared history
      const botResponseText = await sendChatMessage(chatId, history, senderId);

      // Update state with bot response using functional update
      setMessages(prevMessages => {
          const botMessage: Message | null = botResponseText.trim() !== ''
              ? { text: botResponseText, isUser: false, id: uuidv4(), timestamp: new Date() }
              : null;

          if (botMessage) {
              const finalMessages = [...prevMessages, botMessage];
              // Enforce maxMessages limit again after adding bot response
              if (finalMessages.length > maxMessages) {
                 return finalMessages.slice(-maxMessages);
              }
              return finalMessages;
          } 
          // If bot response is empty, return previous state (which already includes user message)
          return prevMessages; 
      });

    } catch (error: any) {
      console.error('Error in sendMessage:', error);
      
      // Fallback responses for EFSoft website when API is unavailable
      const fallbackResponses = [
        "Thank you for contacting EFSoft! How can I help you with our software development services?",
        "I'd be happy to discuss your project requirements. What kind of application are you looking to build?",
        "EFSoft specializes in web applications using modern technologies. Would you like to know more about our services?",
        "Great question! Let me connect you with one of our specialists who can provide detailed information.",
        "I understand you're interested in our services. Feel free to call us at +44 7935 501 848 or email emil.filip@efsoft.co.uk for a detailed discussion."
      ];

      const randomResponse = fallbackResponses[Math.floor(Math.random() * fallbackResponses.length)];
      
      setMessages(prevMessages => {
        const botMessage: Message = { text: randomResponse, isUser: false, id: uuidv4(), timestamp: new Date() };
        const finalMessages = [...prevMessages, botMessage];
        return finalMessages.slice(-maxMessages);
      });

      if (error.message === 'CONTENT_FILTER_ERROR') {
        setError('Your message was flagged by our content filter. Please try rephrasing your question.');
      } else {
        // Don't show error for fallback responses
        console.warn('Using fallback response due to API error:', error.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [messages, chatId, senderId, maxMessages]);

  return {
    messages,
    sendMessage,
    isLoading,
    error
  };
};
